import type { Dataset } from '@colplat/core-client';
import type {
  AccountPurposeRow, AccountRow, AttachmentRow, BankAccountRow, BankConnectionRow, BudgetRow,
  CurrencyRow, Ledger, PurposeRow, TagRow, TransactionBankRefRow, TransactionRow,
  TransactionTagRow, WalletRow, WalletTransferRow,
} from '@colplat/app-webledger/domain';
import { splitsFromTransactions } from '@colplat/app-webledger/domain';

/**
 * The client's `Ledger` (ADR-0025): the local replica, read as rows.
 *
 * Every getter is a synchronous read of an in-memory mirror the `Dataset`
 * already holds, so a screen can compute a whole page of totals inside one
 * render — which is the constraint that decided the store's shape in the
 * first place.
 *
 * **There is no online variant of this.** Every screen reads here whether or
 * not there is a connection; the connection only decides whether `ReplicaSync`
 * is keeping it current. A separate online path would be a path exercised
 * nearly always and an offline one exercised nearly never, and the rarely
 * exercised one is the one that is broken — which is exactly how the feature
 * this replaces came to be broken.
 */
export function datasetLedger(dataset: Dataset): Ledger {
  return {
    accounts: () => dataset.rows<AccountRow>('account'),
    transactions: () => dataset.rows<TransactionRow>('transaction'),
    /*
     * Legs come off the transactions (ADR-0026).
     *
     * `split` is no longer replicated, so there is nothing to fall back to
     * and nothing to reconcile: a device either has the transaction, legs and
     * all, or it does not have it. A store filled before this re-snapshots
     * once — see `REPLICA_SHAPE` — so a transaction without `legs` here is a
     * transaction with no legs, not one whose legs are elsewhere.
     */
    splits: () => splitsFromTransactions(dataset.rows<TransactionRow>('transaction')),
    wallets: () => dataset.rows<WalletRow>('wallet'),
    walletTransfers: () => dataset.rows<WalletTransferRow>('walletTransfer'),
    tags: () => dataset.rows<TagRow>('tag'),
    transactionTags: () => dataset.rows<TransactionTagRow>('transactionTag'),
    budgets: () => dataset.rows<BudgetRow>('budget'),
    currencies: () => dataset.rows<CurrencyRow>('currency'),
    attachments: () => dataset.rows<AttachmentRow>('attachment'),
    purposes: () => dataset.rows<PurposeRow>('purpose'),
    accountPurposes: () => dataset.rows<AccountPurposeRow>('accountPurpose'),
    transactionBankRefs: () => dataset.rows<TransactionBankRefRow>('transactionBankRef'),
    bankAccounts: () => dataset.rows<BankAccountRow>('bankAccount'),
    bankConnections: () => dataset.rows<BankConnectionRow>('bankConnection'),
  };
}
